import { useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  MatchingV2Inconsistency,
  MatchingV2UnmatchedMention,
  UUID,
} from "@/api/types";
import { Button } from "@/components/ui/button";

/**
 * §7.2 of `docs/api/matching-v2.contract.md` — yellow «Несостыковки»
 * pull-out. Renders BOTH `unmatched_mentions[]` (transcript references
 * to non-participants) and `inconsistencies[]` (server-side cross-checks
 * between speakers / participants / candidates).
 *
 * Behaviour:
 *   • Iterates server-provided order; never re-sorts.
 *   • Hides itself entirely when both arrays are empty — the operator's
 *     transcript stays clean for happy-path meetings.
 *   • `mention_not_in_participants` → POST to add-participants endpoint
 *     (delegated to the caller via `onAddParticipant`).
 *   • `participant_silent` → button is disabled with a tooltip — the
 *     «mark absent» flow is reserved for v2.1 (contract §9.2).
 *   • `double_self_intro_conflict` → information-only, no action button.
 */
export interface InconsistenciesBlockProps {
  unmatchedMentions: MatchingV2UnmatchedMention[];
  inconsistencies: MatchingV2Inconsistency[];
  /**
   * Adds an employee to the meeting's participants. Returns a promise
   * so the row can disable its button while the request is in flight.
   * Throws → row surfaces an inline error message.
   */
  onAddParticipant?: (employeeId: UUID) => Promise<void>;
}

export function InconsistenciesBlock({
  unmatchedMentions,
  inconsistencies,
  onAddParticipant,
}: InconsistenciesBlockProps) {
  const { t } = useTranslation();
  if (unmatchedMentions.length === 0 && inconsistencies.length === 0) {
    return null;
  }
  return (
    <section
      className="rounded border border-yellow-300 bg-yellow-50 p-3 text-sm dark:border-yellow-700 dark:bg-yellow-900/30"
      aria-label={t("meetings.card.matchingV2.inconsistencies.title")}
      data-testid="inconsistencies-block"
    >
      <header className="mb-2 text-xs font-medium uppercase tracking-wide text-yellow-900 dark:text-yellow-100">
        {t("meetings.card.matchingV2.inconsistencies.title")}
      </header>
      <ul className="space-y-1.5">
        {unmatchedMentions.map((m, i) => (
          <UnmatchedMentionRow
            key={`mention-${m.surface_text}-${i}`}
            mention={m}
            onAddParticipant={onAddParticipant}
          />
        ))}
        {inconsistencies.map((inc, i) => (
          <InconsistencyRow
            key={`inc-${inc.type}-${i}`}
            inconsistency={inc}
            onAddParticipant={onAddParticipant}
          />
        ))}
      </ul>
    </section>
  );
}

interface UnmatchedMentionRowProps {
  mention: MatchingV2UnmatchedMention;
  onAddParticipant?: (employeeId: UUID) => Promise<void>;
}

function UnmatchedMentionRow({
  mention,
  onAddParticipant,
}: UnmatchedMentionRowProps) {
  const { t } = useTranslation();
  return (
    <li
      className="flex items-center justify-between gap-2"
      data-testid="inconsistency-row"
      data-row-type="unmatched_mention"
    >
      <span>
        {t(
          "meetings.card.matchingV2.inconsistencies.mentionNotInParticipants",
          {
            name: mention.surface_text,
            ending: "",
            pronoun: "его",
          },
        )}
      </span>
      {mention.action_hint === "add_to_participants" &&
        mention.suggestion !== null && (
          <AddParticipantButton
            employeeId={mention.suggestion.employee_id}
            onAddParticipant={onAddParticipant}
          />
        )}
    </li>
  );
}

interface InconsistencyRowProps {
  inconsistency: MatchingV2Inconsistency;
  onAddParticipant?: (employeeId: UUID) => Promise<void>;
}

function InconsistencyRow({
  inconsistency,
  onAddParticipant,
}: InconsistencyRowProps) {
  const { t } = useTranslation();
  if (inconsistency.type === "mention_not_in_participants") {
    return (
      <li
        className="flex items-center justify-between gap-2"
        data-testid="inconsistency-row"
        data-row-type="mention_not_in_participants"
      >
        <span>
          {t(
            "meetings.card.matchingV2.inconsistencies.mentionNotInParticipants",
            {
              name:
                inconsistency.employee_full_name ??
                t("meetings.card.matchingV2.summaryBar.unknownName"),
              ending: "а",
              pronoun: "её",
            },
          )}
        </span>
        {inconsistency.action_hint === "add_to_participants" &&
          inconsistency.employee_id !== undefined && (
            <AddParticipantButton
              employeeId={inconsistency.employee_id}
              onAddParticipant={onAddParticipant}
            />
          )}
      </li>
    );
  }
  if (inconsistency.type === "participant_silent") {
    return (
      <li
        className="flex items-center justify-between gap-2"
        data-testid="inconsistency-row"
        data-row-type="participant_silent"
      >
        <span>
          {t("meetings.card.matchingV2.inconsistencies.participantSilent", {
            name:
              inconsistency.employee_full_name ??
              t("meetings.card.matchingV2.summaryBar.unknownName"),
            ending: "",
          })}
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled
          title={t(
            "meetings.card.matchingV2.inconsistencies.markAbsentDisabledTooltip",
          )}
          data-testid="inconsistency-mark-absent"
        >
          {t("meetings.card.matchingV2.inconsistencies.markAbsentButton")}
        </Button>
      </li>
    );
  }
  // double_self_intro_conflict — information-only per contract §7.2.
  return (
    <li
      className="flex items-center gap-2"
      data-testid="inconsistency-row"
      data-row-type="double_self_intro_conflict"
    >
      <span>
        {t("meetings.card.matchingV2.inconsistencies.doubleSelfIntro", {
          label: inconsistency.speaker_label ?? "?",
        })}
      </span>
    </li>
  );
}

interface AddParticipantButtonProps {
  employeeId: UUID;
  onAddParticipant?: (employeeId: UUID) => Promise<void>;
}

/**
 * Disables itself in-flight + surfaces a typed error in place of the
 * button on failure. Kept inside this module so the `onAddParticipant`
 * contract stays unidirectional (parent owns the mutation, child owns
 * the UX feedback).
 */
function AddParticipantButton({
  employeeId,
  onAddParticipant,
}: AddParticipantButtonProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<"idle" | "pending" | "error">("idle");
  const handleClick = async () => {
    if (onAddParticipant === undefined) return;
    setState("pending");
    try {
      await onAddParticipant(employeeId);
      setState("idle");
    } catch {
      setState("error");
    }
  };
  if (state === "error") {
    return (
      <span
        className="text-xs text-destructive"
        role="alert"
        data-testid="inconsistency-add-error"
      >
        {t("meetings.card.matchingV2.inconsistencies.addError")}
      </span>
    );
  }
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleClick}
      disabled={state === "pending" || onAddParticipant === undefined}
      data-testid="inconsistency-add-btn"
      data-employee-id={employeeId}
    >
      {t("meetings.card.matchingV2.inconsistencies.addButton")}
    </Button>
  );
}
